import {
  Avatar,
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Icon,
  IconButton,
  SelectionList,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import {
  AsyncStatus,
  ReadAdvancedSearchResultsApiArg,
  SearchFilterCondition,
  SearchUserHit,
} from 'lib-shared-types-api';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { rolesApi } from 'apis/roles';
import { searchApi } from 'apis/search';
import { teamsApi } from 'apis/teams';

import {
  FilterDropdownTitle,
  FilterSearchInput,
  LoadingItem,
} from './components';
import { useDropdownToggle } from './hooks/useDropdownToggle';
import styles from './searchControls.module.scss';
import { hasActiveFilters } from './utils';

export const statuses = [
  { label: 'Working agents', value: 'working' },
  { label: 'Away agents', value: 'away' },
  { label: 'Online agents', value: 'online' },
  { label: 'Offline agents', value: 'offline' },
  { label: 'Wrap-up agents', value: 'wrap-up' },
  { label: 'Favorite agents', value: 'favorite' },
  { label: 'Assigned agents', value: 'assigned' },
  { label: 'Unassigned agents', value: 'unassigned' },
] as const;

export type Status = (typeof statuses)[number]['value'];

export const VALID_STATUSES: readonly Status[] = statuses.map((s) => s.value);

export type ActiveFilters = {
  status: Status | null;
  team: string[];
  agent: string[];
  role: string[];
};

type SearchControlsProps = {
  activeFilters: ActiveFilters;
  setActiveFilters: Dispatch<SetStateAction<ActiveFilters>>;
  onRefreshMembers: () => void;
  isRefreshing: boolean;
};

export const SearchControls = ({
  activeFilters,
  setActiveFilters,
  onRefreshMembers,
  isRefreshing,
}: SearchControlsProps): JSX.Element => {
  const [teamOpened, setTeamOpened] = useState<boolean>(false);
  const [roleOpened, setRoleOpened] = useState<boolean>(false);
  const [agentOpened, setAgentOpened] = useState<boolean>(false);
  const [agentPage, setAgentPage] = useState<number>(1);
  const [allAgents, setAllAgents] = useState<SearchUserHit[]>([]);
  const [agentsAsyncStatus, setAgentsAsyncStatus] =
    useState<AsyncStatus>('uninitialized');
  const [agentSearchQuery, setAgentSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [teamSearchQuery, setTeamSearchQuery] = useState<string>('');

  useDebounce(() => setDebouncedSearchQuery(agentSearchQuery), 300, [
    agentSearchQuery,
  ]);

  const handleTeamDropdownToggle = useDropdownToggle(
    setTeamSearchQuery,
    setTeamOpened,
  );

  const handleAgentDropdownToggle = useDropdownToggle(
    setAgentSearchQuery,
    setAgentOpened,
  );

  const { teams, teamsLoading } = teamsApi.useReadTeamsQuery(
    {},
    {
      skip: !teamOpened,
      selectFromResult: ({ data, isFetching }) => ({
        teams: data?.data.teams ?? [],
        teamsLoading: isFetching,
      }),
    },
  );

  const filteredTeams = useMemo(() => {
    if (!teamSearchQuery.trim()) return teams;
    const query = teamSearchQuery.toLowerCase();
    return teams.filter((team) => team.name.toLowerCase().includes(query));
  }, [teams, teamSearchQuery]);

  const { systemRoles, customRoles, rolesLoading } = rolesApi.useReadRolesQuery(
    undefined,
    {
      skip: !roleOpened,
      selectFromResult: ({ data, isFetching }) => {
        const roles = data?.data.roles ?? [];
        return {
          systemRoles: roles
            .filter((role) => role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          customRoles: roles
            .filter((role) => !role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          rolesLoading: isFetching,
        };
      },
    },
  );

  const agentSearchParams: ReadAdvancedSearchResultsApiArg = useMemo(
    () => ({
      params: {
        scope: 'users',
        query: debouncedSearchQuery,
        filters: {
          strategy: 'all',
          conditions: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ label: '', value: 'member' }],
            } satisfies SearchFilterCondition,
            ...(activeFilters.team.length > 0
              ? [
                  {
                    field: 'teams',
                    operator: 'is_one',
                    values: activeFilters.team.map((teamId) => ({
                      label: '',
                      value: teamId,
                    })),
                  } satisfies SearchFilterCondition,
                ]
              : []),
          ],
        },
        sort: { field: 'display_name', order: 'asc' },
        strict: false,
        page: agentPage,
      },
    }),
    [agentPage, activeFilters.team, debouncedSearchQuery],
  );

  const { agents, agentsLoading, hasMoreAgents } =
    searchApi.useReadAdvancedSearchResultsQuery(agentSearchParams, {
      skip: !agentOpened,
      selectFromResult: ({ data, isFetching }) => {
        const userResult = data?.data.results.find(
          (result) => result.hits_type === 'user',
        );
        const currentPageAgents =
          userResult && userResult.hits_type === 'user' ? userResult.hits : [];
        const totalPages = userResult?.nb_pages ?? 1;

        return {
          agents: currentPageAgents,
          agentsLoading: isFetching,
          hasMoreAgents: agentPage < totalPages,
        };
      },
    });

  // Track team filter as a string for stable comparison
  const teamFilterKey = activeFilters.team.join(',');

  // Reset agent pagination when team filter or search query changes
  useEffect(() => {
    setAgentPage(1);
    setAllAgents([]);
    setAgentsAsyncStatus('uninitialized');
  }, [teamFilterKey, debouncedSearchQuery]);

  // Accumulate agents when new page data arrives
  useEffect(() => {
    if (
      agents.length > 0 &&
      !agentsLoading &&
      agentsAsyncStatus === 'pending'
    ) {
      setAllAgents((prev) => {
        // If it's the first page, replace all agents
        if (agentPage === 1) {
          return [...agents];
        }
        // Otherwise, append new agents (avoiding duplicates)
        const existingIds = new Set(prev.map((a) => a.id));
        const newAgents = agents.filter((a) => !existingIds.has(a.id));
        return [...prev, ...newAgents];
      });
      setAgentsAsyncStatus('fulfilled');
    }
  }, [agents, agentPage, agentsLoading, agentsAsyncStatus]);

  // Set pending status when agentsLoading becomes true, or when cached data is available
  useEffect(() => {
    if (
      agentsAsyncStatus === 'uninitialized' &&
      (agentsLoading || agents.length > 0)
    ) {
      // Set to pending - the accumulation effect will handle the data
      setAgentsAsyncStatus('pending');
    }
  }, [agentsLoading, agentsAsyncStatus, agents]);

  const handleLoadMoreAgents = useCallback(() => {
    if (agentsAsyncStatus === 'fulfilled' && hasMoreAgents) {
      setAgentsAsyncStatus('uninitialized');
      setAgentPage((prev) => prev + 1);
    }
  }, [agentsAsyncStatus, hasMoreAgents]);

  const handleClearFilters = useCallback(() => {
    setActiveFilters({ status: null, team: [], agent: [], role: [] });
  }, [setActiveFilters]);

  return (
    <>
      {hasActiveFilters(activeFilters) && (
        <Button variant="tertiary" icon="close" onClick={handleClearFilters}>
          Clear filters
        </Button>
      )}
      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
        }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <div className={styles.dropdownScroll}>
                <SelectionList
                  isMultiple={false}
                  selectedValues={
                    activeFilters.status ? [activeFilters.status] : []
                  }
                  onChange={(e) => {
                    const newStatus = e.selectedValues[0] as Status | undefined;
                    setActiveFilters((prev) => ({
                      ...prev,
                      status:
                        newStatus === prev.status ? null : (newStatus ?? null),
                    }));
                    toggleDropdown();
                  }}
                >
                  <FilterDropdownTitle>Filter by status</FilterDropdownTitle>
                  {statuses.map((status) => (
                    <SelectionList.Item
                      key={status.value}
                      value={status.value}
                      contentClassName={styles.itemContent}
                    >
                      {status.label}
                    </SelectionList.Item>
                  ))}
                </SelectionList>
              </div>
            )}
          </DropdownButtonContext.Consumer>
        }
      >
        {activeFilters.status
          ? statuses.find((s) => s.value === activeFilters.status)?.label
          : 'Status'}
      </DropdownButton>
      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.team.length > 0
              ? `${activeFilters.team.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleTeamDropdownToggle}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by team</FilterDropdownTitle>
            <FilterSearchInput
              value={teamSearchQuery}
              onChange={setTeamSearchQuery}
              placeholder="Search teams"
            />
            <SelectionList
              selectedValues={activeFilters.team}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  team: e.selectedValues,
                }))
              }
            >
              {teamsLoading ? (
                <LoadingItem />
              ) : (
                filteredTeams.map((team) => (
                  <SelectionList.Item
                    key={team.id}
                    value={team.id}
                    contentClassName={styles.itemContent}
                  >
                    {team.name}
                  </SelectionList.Item>
                ))
              )}
            </SelectionList>
          </div>
        }
      >
        <FlexBox alignItems="center" gap="100">
          Team
        </FlexBox>
      </DropdownButton>
      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.role.length > 0
              ? `${activeFilters.role.length}`
              : undefined,
        }}
        onToggle={setRoleOpened}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by role</FilterDropdownTitle>
            <SelectionList
              selectedValues={activeFilters.role}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  role: e.selectedValues,
                }))
              }
            >
              {rolesLoading ? (
                <LoadingItem />
              ) : (
                <>
                  {systemRoles.map((role) => (
                    <SelectionList.Item
                      key={role.id}
                      value={role.id}
                      contentClassName={styles.itemContent}
                    >
                      <Icon
                        name="role-system"
                        color={typography.color.tertiary}
                        size={16}
                      />
                      {role.name}
                    </SelectionList.Item>
                  ))}
                  {systemRoles.length > 0 && customRoles.length > 0 && (
                    <Divider />
                  )}
                  {customRoles.map((role) => (
                    <SelectionList.Item
                      key={role.id}
                      value={role.id}
                      contentClassName={styles.itemContent}
                    >
                      <Icon
                        name="role-custom"
                        color={typography.color.primary}
                        size={16}
                      />
                      {role.name}
                    </SelectionList.Item>
                  ))}
                </>
              )}
            </SelectionList>
          </div>
        }
      >
        <FlexBox alignItems="center" gap="100">
          Role
        </FlexBox>
      </DropdownButton>
      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.agent.length > 0
              ? `${activeFilters.agent.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleAgentDropdownToggle}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by agent</FilterDropdownTitle>
            <FilterSearchInput
              value={agentSearchQuery}
              onChange={setAgentSearchQuery}
              placeholder="Search agents"
            />
            <SelectionList
              selectedValues={activeFilters.agent}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  agent: e.selectedValues,
                }))
              }
            >
              {allAgents.map((agent) => (
                <SelectionList.Item
                  key={agent.id}
                  value={agent.id}
                  contentClassName={styles.itemContent}
                >
                  <Avatar
                    user={{
                      display_name: agent.display_name,
                      avatar_url: agent.avatar_url,
                      email: agent.email,
                      phone_number: agent.phone_number,
                    }}
                    size="24"
                  />
                  <span>{agent.display_name}</span>
                </SelectionList.Item>
              ))}
              {agentsLoading && <LoadingItem />}
              {agentsAsyncStatus === 'fulfilled' && hasMoreAgents && (
                <Waypoint onEnter={handleLoadMoreAgents} />
              )}
            </SelectionList>
          </div>
        }
      >
        <FlexBox alignItems="center" gap="100">
          Agent
        </FlexBox>
      </DropdownButton>
      <Tooltip content="Refresh">
        <IconButton
          icon="sync"
          variant="tertiary"
          isLoading={isRefreshing}
          onClick={onRefreshMembers}
        />
      </Tooltip>
    </>
  );
};
